import PrintMethodToggle from '@/components/print/PrintMethodToggle';
import React, { useState, useRef, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { ArrowLeft, Scan, Printer, Package, AlertTriangle, CheckCircle, History, RefreshCw, Usb, Settings, Search, Tag, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import apiClient from '@/services/apiClient';
import * as printerApi from '@/services/printerApi';
import { useUSNReplacement } from '@/hooks/useUSNReplacement';

const FGUSNLabelReprint = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const scanInputRef = useRef(null);
  const { replace, openReplaceDialog } = useUSNReplacement();
  
  // Check for design transfer labels from navigation
  const designTransferLabels = location.state?.designTransferLabels || [];
  const fromDesignTransfer = location.state?.fromDesignTransfer || false;
  // Preset USN from deep-linking (e.g., USN Status Report → Reprint Sticker)
  const presetUsn = location.state?.presetUsn || '';
  // Damage Section reprint flow — applies different defaults (Pricing=TN, Template=DHOTI - FG)
  const fromDamage = location.state?.fromDamage === true;
  // 2026-05-26 — Bulk Reprint Contaminated USNs (from FGPutAwaySupervisor).
  // The supervisor page pushes `presetBatch` (array of {usn_code,
  // pair_number, is_combo, is_contaminated}) + `sourceFlow` so the
  // manual-batch queue lights up pre-populated and the backend gets
  // tagged with the explicit recovery flow.
  const presetBatch = Array.isArray(location.state?.presetBatch) ? location.state.presetBatch : [];
  const presetSourceFlow = location.state?.sourceFlow || '';
  
  // State
  const [usnCode, setUsnCode] = useState('');
  const [isSearching, setIsSearching] = useState(false);
  const [isPrinting, setIsPrinting] = useState(false);
  const [processingStatus, setProcessingStatus] = useState('');
  const [usnDetails, setUsnDetails] = useState(null);
  const [showHistory, setShowHistory] = useState(false);
  const [reprintHistory, setReprintHistory] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  
  // Design Transfer batch printing state
  const [dtLabelsQueue, setDtLabelsQueue] = useState([]);
  const [dtPrintingIndex, setDtPrintingIndex] = useState(-1);
  // 2026-05-16 — Manual scan batch queue. Operators can scan many USNs
  // in succession, accumulate them in this queue, then click "Print
  // Batch (N)" to invoke the same two-phase combo print pipeline used
  // by Design Transfer. Combo USNs in the batch trigger Phase B
  // (box-sticker) prompt automatically.
  const [manualBatchQueue, setManualBatchQueue] = useState([]);
  const [batchMode, setBatchMode] = useState(false);
  const [batchAddingUsn, setBatchAddingUsn] = useState(false);
  // 2026-05-15 — REPLACE pool depth indicator. Every reprint mints a
  // fresh USN from this pool. Surfacing pool depth on-page lets the
  // operator KNOW BEFORE clicking Print All if the batch will fail
  // due to an empty pool (the most common cause of "Replace failed").
  const [replacePool, setReplacePool] = useState(null); // { available, used, total }
  // 2026-05-14 — Two-phase Design Transfer batch print. Operator picks
  // pricing + product template + product printer in Phase A; the loop
  // collects per-USN product + box label data; then a Phase B prompt
  // requires box template + box printer before the box-sticker blob
  // is streamed (only when at least one combo USN exists in the batch).
  const [dtPhaseAOpen, setDtPhaseAOpen] = useState(false);
  const [dtPhaseAResolve, setDtPhaseAResolve] = useState(null);
  const [dtPhaseAPricing, setDtPhaseAPricing] = useState('');
  const [dtPhaseATemplate, setDtPhaseATemplate] = useState('');
  const [dtPhaseADevice, setDtPhaseADevice] = useState(null);
  const [dtPhaseAConnecting, setDtPhaseAConnecting] = useState(false);
  const [dtPhaseBOpen, setDtPhaseBOpen] = useState(false);
  const [dtPhaseBResolve, setDtPhaseBResolve] = useState(null);
  const [dtPhaseBComboCount, setDtPhaseBComboCount] = useState(0);
  const [dtPhaseBTemplate, setDtPhaseBTemplate] = useState('');
  const [dtPhaseBDevice, setDtPhaseBDevice] = useState(null);
  const [dtPhaseBConnecting, setDtPhaseBConnecting] = useState(false);
  
  // Pricing Type State
  const [pricingType, setPricingType] = useState('');
  const [availablePricingTypes, setAvailablePricingTypes] = useState([]);
  
  // Settings
  const [templates, setTemplates] = useState([]);
  const [selectedTemplate, setSelectedTemplate] = useState('');
  // Optional Box Template — when set on a single-scan reprint, the same
  // replaced USN is printed twice: once with the Product (selectedTemplate)
  // sticker, then once with the Box (boxTemplate) sticker after the user
  // confirms the box-printer in a follow-up dialog.
  const [boxTemplate, setBoxTemplate] = useState('');
  const [pendingBoxPrint, setPendingBoxPrint] = useState(null); // { newUsn, oldUsn, designName }
  const [printerModel, setPrinterModel] = useState('TSC_TE210');
  const [usbDevice, setUsbDevice] = useState(null);
  const [usbSupported, setUsbSupported] = useState(false);
  
  // Line spacing multiplier
  const [arialLineSpacingMultiplier, setArialLineSpacingMultiplier] = useState(2.5);
  
  // Last reprinted USN
  const [lastReprinted, setLastReprinted] = useState(null);
  
  // Session stats
  const [sessionStats, setSessionStats] = useState({ reprinted: 0, failed: 0 });
  
  // Printer configurations
  const PRINTER_CONFIGS = {
    TSC_TE210: {
      name: 'TSC TE210 (Desktop)',
      dpi: 203,
      speed: 4,
      density: 8,
      vendorId: 0x1203
    },
    TSC_MH240: {
      name: 'TSC MH240 (Industrial)',
      dpi: 203,
      speed: 6,
      density: 10,
      vendorId: 0x1203
    }
  };
  
  useEffect(() => {
    scanInputRef.current?.focus();
    setUsbSupported(printerApi.isWebUSBSupported());
    fetchTemplates();
    fetchPrintSettings();
    // 2026-05-15 — Pre-fetch REPLACE pool depth so we can warn the
    // operator if the batch print will fail due to empty pool.
    apiClient.get('/usn-replacement/pool-status')
      .then((r) => setReplacePool(r.data))
      .catch(() => setReplacePool(null));
  }, []);
  
  // Handle design transfer labels from navigation
  useEffect(() => {
    if (fromDesignTransfer && designTransferLabels.length > 0) {
      setDtLabelsQueue(designTransferLabels);
      toast.info(`${designTransferLabels.length} design transfer labels ready to print. Connect USB printer and click "Print All".`);
    }
  }, [fromDesignTransfer, designTransferLabels]);

  // Handle preset USN from deep-linking (auto-populate + auto-search)
  useEffect(() => {
    if (presetUsn) {
      setUsnCode(presetUsn);
      // Defer search slightly so state updates flush; handleSearchUSN reads from usnCode state
      setTimeout(() => {
        (async () => {
          try {
            const code = String(presetUsn).trim().toUpperCase();
            setIsSearching(true);
            const response = await apiClient.get(`/main-labels/usn-reprint/lookup/${encodeURIComponent(code)}`);
            if (response.data.found) {
              setUsnDetails(response.data);
              applyPostLookupDefaults(response.data);
              toast.success(`USN ${code} pre-loaded — connect USB printer and click Reprint.`);
            } else {
              toast.error(`USN ${code} not found — please verify.`);
            }
          } catch (err) {
            toast.error(err.response?.data?.detail || 'Failed to auto-load preset USN');
          } finally {
            setIsSearching(false);
          }
        })();
      }, 150);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [presetUsn]);
  
  const fetchTemplates = async () => {
    try {
      const response = await apiClient.get('/main-labels/templates');
      setTemplates(response.data.templates || []);
    } catch (error) {
      console.error('Error fetching templates:', error);
    }
  };

  // Find DHOTI - FG template id (used as default product template for damage reprint)
  const findDhotiFgTemplateId = () => {
    const t = templates.find(
      (x) => /^\s*dhoti\s*-\s*fg\s*$/i.test(String(x.template_name || ''))
    );
    return t ? t.template_id : '';
  };

  // Apply post-lookup defaults: damage reprint forces Pricing=TN + Template=DHOTI - FG;
  // standard flow falls back to the USN's original pricing/template.
  const applyPostLookupDefaults = (data) => {
    setAvailablePricingTypes(data.available_pricing_types || []);
    if (fromDamage) {
      setPricingType('TN');
      setSelectedTemplate(findDhotiFgTemplateId());
      // Box template stays blank — operator selects only if it's a box item.
    } else {
      setPricingType(data.original_pricing_type || data.available_pricing_types?.[0] || 'TN');
      setSelectedTemplate(data.original_template_id || '');
    }
  };

  // If templates load AFTER a USN is already searched in damage mode,
  // back-fill the DHOTI - FG default once available.
  useEffect(() => {
    if (fromDamage && templates.length > 0 && !selectedTemplate) {
      const id = findDhotiFgTemplateId();
      if (id) setSelectedTemplate(id);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [templates, fromDamage]);
  
  const fetchPrintSettings = async () => {
    try {
      const response = await apiClient.get('/print-settings');
      if (response.data.settings?.arial_line_spacing_multiplier) {
        setArialLineSpacingMultiplier(response.data.settings.arial_line_spacing_multiplier);
      }
    } catch (error) {
      console.error('Error fetching print settings:', error);
    }
  };
  
  // Generate TSPL commands
  const generateTsplFromLabelData = (labelData, template, usnCode, mrpFormatted, displayUsn = null, printerConfig = null, templateType = null) => {
    const config = printerConfig || PRINTER_CONFIGS.TSC_TE210;
    const widthMm = template ? Math.round(template.label_width_dots / 8) : 100;
    const heightMm = template ? Math.round(template.label_height_dots / 8) : 80;
    const usnDisplay = displayUsn || usnCode;
    const fields = template?.fields || {};
    const tplType = templateType || template?.template_type || '';
    
    const getField = (key, defaultX = 0, defaultY = 0, defaultFontSize = 2, defaultSize = 4) => {
      const field = fields[key] || {};
      return {
        x: field.x ?? defaultX,
        y: field.y ?? defaultY,
        font_size: field.font_size ?? defaultFontSize,
        bold: field.bold ?? false,
        size: field.size ?? defaultSize,
        text: field.text || ''
      };
    };
    
    const generateTextCmd = (x, y, fontSize, text) => {
      const arialSize = fontSize || 10.5;
      return `TEXT ${x},${y},"ARIALB.TTF",0,${arialSize},${arialSize},"${text}"\r\n`;
    };
    
    let tspl = '';
    
    tspl += `SIZE ${widthMm} mm, ${heightMm} mm\r\n`;
    tspl += `GAP 3 mm, 0 mm\r\n`;
    tspl += `SPEED ${config.speed}\r\n`;
    tspl += `DENSITY ${config.density}\r\n`;
    tspl += `DIRECTION 1,0\r\n`;
    tspl += `REFERENCE 0,0\r\n`;
    tspl += `OFFSET 0 mm\r\n`;
    tspl += `SET PEEL OFF\r\n`;
    tspl += `SET CUTTER OFF\r\n`;
    tspl += `SET PARTIAL_CUTTER OFF\r\n`;
    tspl += `CLS\r\n`;
    
    // QUALITY
    const qualityLabel = getField('quality_label', 16, 16, 10.5);
    const qualityValue = getField('quality_value', 160, 16, 14.0);
    if (labelData.quality) {
      tspl += generateTextCmd(qualityLabel.x, qualityLabel.y, qualityLabel.font_size, qualityLabel.text || 'QUALITY');
      tspl += generateTextCmd(qualityValue.x, qualityValue.y, qualityValue.font_size, labelData.quality);
    }
    
    // COLOR
    if (labelData.color) {
      const colorLabel = getField('color_label', 16, 56, 10.5);
      const colorValue = getField('color_value', 160, 56, 10.5);
      tspl += generateTextCmd(colorLabel.x, colorLabel.y, colorLabel.font_size, colorLabel.text || 'COLOR');
      tspl += generateTextCmd(colorValue.x, colorValue.y, colorValue.font_size, labelData.color);
    }
    
    // CONTENT
    const contentLabel = getField('content_label', 16, 96, 10.5);
    const contentValue = getField('content_value', 160, 96, 14.0);
    if (labelData.content1) {
      let contentParts = [labelData.content1];
      if (labelData.content2) contentParts.push(labelData.content2);
      if (labelData.content3) contentParts.push(labelData.content3);
      const content = contentParts.join('    ');
      tspl += generateTextCmd(contentLabel.x, contentLabel.y, contentLabel.font_size, contentLabel.text || 'CONTENT');
      tspl += generateTextCmd(contentValue.x, contentValue.y, contentValue.font_size, content);
    }
    
    // SIZE
    const sizeLabel = getField('size_label', 16, 136, 10.5);
    const sizeValue = getField('size_value', 160, 136, 10.5);
    let sizeParts = [];
    if (labelData.size1) sizeParts.push(labelData.size1);
    if (labelData.size2) sizeParts.push(labelData.size2);
    if (labelData.size3) sizeParts.push(labelData.size3);
    const size = sizeParts.length > 0 ? sizeParts.join('    ') : (labelData.size_from_rates || '');
    if (size) {
      tspl += generateTextCmd(sizeLabel.x, sizeLabel.y, sizeLabel.font_size, sizeLabel.text || 'SIZE');
      tspl += generateTextCmd(sizeValue.x, sizeValue.y, sizeValue.font_size, size);
    }
    
    // DESC5 Bold for combo
    if (labelData.desc5_bold && tplType?.includes('combo')) {
      const desc5Field = getField('desc5_bold', 400, 56, 14.0);
      tspl += generateTextCmd(desc5Field.x, desc5Field.y, desc5Field.font_size, labelData.desc5_bold);
    }
    
    // QTY (value configurable via template — falls back to label data / default)
    const qtyField = getField('qty', 16, 176, 10.5);
    const qtyVal = template?.fields?.qty?.value;
    const qtyText = (qtyVal !== undefined && qtyVal !== null && qtyVal !== '')
      ? `QTY : ${qtyVal} N`
      : (labelData.qty || 'QTY : 1 N');
    tspl += generateTextCmd(qtyField.x, qtyField.y, qtyField.font_size, qtyText);
    
    // MRP
    const mrpLabel = getField('mrp_label', 200, 176, 10.5);
    const mrpValue = getField('mrp_value', 280, 166, 18.0);
    tspl += generateTextCmd(mrpLabel.x, mrpLabel.y, mrpLabel.font_size, mrpLabel.text || 'MRP :');
    tspl += generateTextCmd(mrpValue.x, mrpValue.y, mrpValue.font_size, mrpFormatted);
    
    // Tax Notice
    const taxNotice = getField('tax_notice', 280, 210, 7.5);
    tspl += generateTextCmd(taxNotice.x, taxNotice.y, taxNotice.font_size, '(INCL. OF ALL TAXES)');
    
    // MADE IN INDIA
    const madeInIndia = getField('made_in_india', 16, 250, 10.5);
    tspl += generateTextCmd(madeInIndia.x, madeInIndia.y, madeInIndia.font_size, 'MADE IN INDIA');
    
    // QR Code 1 (USN)
    const qr1 = getField('qr1', 16, 290, 10.5, 5);
    const qr1Text = getField('qr1_text', 16, 410, 10.5);
    if (usnCode) {
      tspl += `QRCODE ${qr1.x},${qr1.y},L,${qr1.size},A,0,"${usnCode}"\r\n`;
      tspl += generateTextCmd(qr1Text.x, qr1Text.y, qr1Text.font_size, usnDisplay);
    }
    
    // QR Code 2 (Barcode)
    const qr2 = getField('qr2', 200, 290, 10.5, 5);
    const qr2Text = getField('qr2_text', 200, 410, 10.5);
    if (labelData.qr2_code) {
      tspl += `QRCODE ${qr2.x},${qr2.y},L,${qr2.size},A,0,"${labelData.qr2_code}"\r\n`;
      tspl += generateTextCmd(qr2Text.x, qr2Text.y, qr2Text.font_size, labelData.qr2_code);
    }
    
    // Consumer Notice
    const consumerNotice = getField('consumer_notice', 16, 460, 7.5);
    const actualFontSize = consumerNotice.font_size || 7.5;
    const lineHeight = Math.ceil(actualFontSize * arialLineSpacingMultiplier);
    tspl += generateTextCmd(consumerNotice.x, consumerNotice.y, consumerNotice.font_size, 'Note: Consumer is free to open and');
    tspl += generateTextCmd(consumerNotice.x, consumerNotice.y + lineHeight, consumerNotice.font_size, 'inspect the product before buying it');
    
    tspl += `PRINT 1,1\r\n`;
    
    return tspl;
  };
  
  // Connect USB printer
  const connectUSBPrinter = async () => {
    try {
      const device = await printerApi.requestUSBPrinter();
      setUsbDevice(device);
      toast.success(`Connected to ${device.productName || 'USB Printer'}`);
      return device;
    } catch (error) {
      console.error('Error connecting USB:', error);
      toast.error('Failed to connect USB printer. Please try again.');
      return null;
    }
  };
  
  // 2026-05-16 — Add a scanned USN to the manual-batch queue.
  // Pre-validates by hitting the same lookup endpoint, then pushes a
  // minimal label record onto the queue and clears the input. Combo
  // detection is deferred to the per-USN /generate call inside the
  // print orchestrator (boxPlans collects combos automatically).
  const handleAddToManualBatch = async () => {
    const code = usnCode.trim().toUpperCase();
    if (!code) {
      toast.error('Please scan or enter a USN');
      return;
    }
    if (manualBatchQueue.some((l) => l.usn_code === code)) {
      toast.info(`${code} is already in the batch`);
      setUsnCode('');
      scanInputRef.current?.focus();
      return;
    }
    setBatchAddingUsn(true);
    try {
      const r = await apiClient.get(`/main-labels/usn-reprint/lookup/${encodeURIComponent(code)}`);
      if (!r.data?.found) {
        toast.error(`USN ${code} not found in master.`);
        return;
      }
      const data = r.data;
      const label = {
        usn_code: code,
        design_name: data.item?.design_name || data.design_name || '',
        shade: data.item?.shade || data.shade || '',
        size: data.item?.size || data.size || '',
        is_combo: Boolean(data.item?.box_template_id || data.box_template_id),
      };
      setManualBatchQueue((q) => [...q, label]);
      toast.success(
        `Added ${code}${label.design_name ? ` · ${label.design_name}` : ''}${label.is_combo ? ' · COMBO' : ''}`,
        { duration: 2000 },
      );
      setUsnCode('');
      scanInputRef.current?.focus();
    } catch (e) {
      const msg = e?.response?.data?.detail || 'Failed to add USN';
      toast.error(msg);
    } finally {
      setBatchAddingUsn(false);
    }
  };
  const handleRemoveFromManualBatch = (usnCodeToRemove) => {
    setManualBatchQueue((q) => q.filter((l) => l.usn_code !== usnCodeToRemove));
  };
  const handleClearManualBatch = () => {
    if (manualBatchQueue.length === 0) return;
    if (window.confirm(`Clear all ${manualBatchQueue.length} USN(s) from the batch?`)) {
      setManualBatchQueue([]);
    }
  };

  // Search USN
  const handleSearchUSN = async () => {
    const code = usnCode.trim().toUpperCase();
    if (!code) {
      toast.error('Please enter a USN code');
      return;
    }
    // 2026-05-16 — In Batch Mode, every scan auto-adds to the queue
    // instead of opening single-USN details. Lets operators rip
    // through 50+ scans without clicking anything.
    if (batchMode) {
      await handleAddToManualBatch();
      return;
    }
    setIsSearching(true);
    setUsnDetails(null);
    
    try {
      const response = await apiClient.get(`/main-labels/usn-reprint/lookup/${encodeURIComponent(code)}`);
      
      if (response.data.found) {
        setUsnDetails(response.data);
        applyPostLookupDefaults(response.data);
        toast.success('USN found! Ready to reprint.');
      } else {
        toast.error('USN Not Found');
      }
    } catch (error) {
      const msg = error.response?.data?.detail || 'Failed to lookup USN';
      toast.error(msg);
      setUsnDetails(null);
    } finally {
      setIsSearching(false);
    }
  };
  
  // Clear search
  const handleClear = () => {
    setUsnCode('');
    setUsnDetails(null);
    setAvailablePricingTypes([]);
    setPricingType('');
    setSelectedTemplate('');
    setBoxTemplate('');
    scanInputRef.current?.focus();
  };

  // Generate label data + TSPL commands and send to USB printer for ONE sticker.
  // Used by both the product sticker and the box sticker passes.
  const printSingleSticker = async (usnForLabel, templateId, device) => {
    const reprintResponse = await apiClient.post('/main-labels/usn-reprint/generate', {
      usn_code: usnForLabel,
      pricing_type: pricingType || undefined,
      template_id: templateId || undefined,
    });
    const printerConfig = PRINTER_CONFIGS[printerModel];
    const { label_data, template, mrp_formatted, usn_code } = reprintResponse.data;
    const commands = generateTsplFromLabelData(
      label_data,
      template,
      usn_code,
      mrp_formatted,
      null,
      printerConfig,
      template?.template_type
    );
    await printerApi.sendToUSBPrinter(device, commands);
    return reprintResponse.data;
  };
  
  // Reprint label
  const handleReprint = async () => {
    if (!usnDetails) {
      toast.error('Please search for a USN first');
      return;
    }
    
    // Check USB device
    let device = usbDevice;
    if (!device) {
      setProcessingStatus('Connecting to USB printer...');
      device = await connectUSBPrinter();
      if (!device) {
        setProcessingStatus('');
        return;
      }
    }
    
    setIsPrinting(true);
    setProcessingStatus('Generating label data...');
    
    try {
      // STEP 1: Ask user to confirm replacement (deactivates old, claims new)
      const dialog = await openReplaceDialog({
        count: 1,
        sourceFlow: 'fg_usn_reprint',
        subjectLabel: `${usnDetails.usn_code} • ${usnDetails.design_name || ''}`.trim(),
      });
      if (!dialog) {
        setIsPrinting(false); setProcessingStatus('');
        return;
      }
      setProcessingStatus('Issuing replacement USN…');
      const replaced = await replace(usnDetails.usn_code, {
        reason: dialog.reason,
        sourceFlow: 'fg_usn_reprint',
        linkedDocuments: { source_page: 'FGUSNLabelReprint' },
      });
      if (!replaced) { setIsPrinting(false); setProcessingStatus(''); return; }

      // STEP 2: Print the PRODUCT sticker for the NEW USN.
      setProcessingStatus('Printing product sticker...');
      const productData = await printSingleSticker(replaced.new_usn, selectedTemplate, device);
      const newUsn = productData.usn_code;
      const designName = productData.item?.design_name || usnDetails.item?.design_name;

      // STEP 3: If a Box Template is selected, queue the box sticker print.
      // The operator confirms / connects the box-sticker printer in a follow-up dialog.
      if (boxTemplate) {
        setPendingBoxPrint({
          newUsn,
          oldUsn: usnDetails.usn_code,
          designName,
        });
        toast.success(`Product sticker printed. Now print Box sticker for ${newUsn}.`);
      } else {
        // Single-sticker flow done — record success and clear.
        setLastReprinted({
          usn_code: newUsn,
          old_usn_code: usnDetails.usn_code,
          design_name: designName,
          timestamp: new Date(),
        });
        setSessionStats(prev => ({ ...prev, reprinted: prev.reprinted + 1 }));
        toast.success(`Replaced ${usnDetails.usn_code} → ${newUsn} and printed.`);
        handleClear();
      }
      
    } catch (error) {
      console.error('Reprint error:', error);
      setSessionStats(prev => ({ ...prev, failed: prev.failed + 1 }));
      const msg = error.response?.data?.detail || error.message || 'Failed to reprint';
      toast.error(msg);
    } finally {
      setIsPrinting(false);
      setProcessingStatus('');
    }
  };

  // Print the BOX sticker for the already-replaced USN.
  // Operator can connect a different USB printer first, or reuse the current one.
  const handlePrintBoxSticker = async (forceReconnect = false) => {
    if (!pendingBoxPrint || !boxTemplate) return;
    let device = usbDevice;
    if (forceReconnect || !device) {
      setProcessingStatus('Connecting to box-sticker printer...');
      device = await connectUSBPrinter();
      if (!device) { setProcessingStatus(''); return; }
    }
    setIsPrinting(true);
    setProcessingStatus('Printing box sticker...');
    try {
      await printSingleSticker(pendingBoxPrint.newUsn, boxTemplate, device);
      setLastReprinted({
        usn_code: pendingBoxPrint.newUsn,
        old_usn_code: pendingBoxPrint.oldUsn,
        design_name: pendingBoxPrint.designName,
        timestamp: new Date(),
      });
      setSessionStats(prev => ({ ...prev, reprinted: prev.reprinted + 1 }));
      toast.success(`Box sticker printed for ${pendingBoxPrint.newUsn}.`);
      setPendingBoxPrint(null);
      handleClear();
    } catch (error) {
      console.error('Box print error:', error);
      const msg = error.response?.data?.detail || error.message || 'Failed to print box sticker';
      toast.error(msg);
    } finally {
      setIsPrinting(false);
      setProcessingStatus('');
    }
  };

  const handleSkipBoxSticker = () => {
    if (!pendingBoxPrint) return;
    setLastReprinted({
      usn_code: pendingBoxPrint.newUsn,
      old_usn_code: pendingBoxPrint.oldUsn,
      design_name: pendingBoxPrint.designName,
      timestamp: new Date(),
    });
    setSessionStats(prev => ({ ...prev, reprinted: prev.reprinted + 1 }));
    toast.message('Box sticker skipped.');
    setPendingBoxPrint(null);
    handleClear();
  };
  
  // Load reprint history
  const loadReprintHistory = async () => {
    setHistoryLoading(true);
    try {
      const response = await apiClient.get('/main-labels/usn-reprint/history?limit=50');
      setReprintHistory(response.data.history || []);
    } catch (error) {
      toast.error('Failed to load reprint history');
    } finally {
      setHistoryLoading(false);
    }
  };
  
  const handleShowHistory = () => {
    setShowHistory(true);
    loadReprintHistory();
  };
  
  // Print all queued labels — two-phase (product → box).
  // 2026-05-14 — Combo articles need both a product sticker and a box
  // sticker on different rolls. New flow:
  //   Phase A prompt → pricing + product template + product printer.
  //   Bulk USN replace.
  //   Per-USN: fetch label_data + product template + box_template via
  //            /main-labels/usn-reprint/generate. Build innerBlob; if the
  //            USN has a box_template, also build a boxBlob entry.
  //   Stream innerBlob to product printer.
  //   If any combos exist → Phase B prompt → box template + box printer.
  //   Stream boxBlob to box printer.
  // 2026-05-16 — Parameterized to support BOTH the Design Transfer
  // server-fed queue AND the new manually-scanned batch queue. The
  // caller passes its own queue + setter + source_flow so we don't
  // duplicate ~200 lines of print orchestration code.
  const _handlePrintQueue = async ({
    queue, setQueue, sourceFlow, subjectLabel, queueLabel,
  }) => {
    if (queue.length === 0) {
      toast.info('No labels to print');
      return;
    }

    // ---- PHASE A: pricing + product template + product printer ----
    if (templates.length === 0) {
      toast.error('Label templates not loaded yet — please retry in a moment.');
      return;
    }
    const phaseA = await new Promise((resolve) => {
      setDtPhaseAPricing(pricingType || availablePricingTypes[0] || 'TN');
      setDtPhaseATemplate(selectedTemplate || templates[0]?.template_id || '');
      setDtPhaseADevice(usbDevice || null);
      setDtPhaseAResolve(() => resolve);
      setDtPhaseAOpen(true);
    });
    if (!phaseA || phaseA.cancelled) {
      toast.info('Print cancelled — labels remain queued.');
      return;
    }
    const productDevice = phaseA.device;
    const productTemplateId = phaseA.templateId;
    const chosenPricing = phaseA.pricing;
    setUsbDevice(productDevice);

    // ---- STEP 1: Bulk-replace all queued USNs in one shot. ----
    const dialog = await openReplaceDialog({
      count: queue.length,
      sourceFlow,
      subjectLabel: subjectLabel || `${queueLabel || 'Batch'} relabel — ${queue.length} USN(s)`,
    });
    if (!dialog) return;
    const oldUsns = queue.map((l) => l.usn_code);
    // 2026-06-23 — CLIENT-SIDE CHUNKING (fixes recurring HTTP 504).
    // A single `/replace-batch` for the whole queue returns only after
    // EVERY USN finishes, so a large batch on production Atlas blows past
    // Cloudflare's ~100s "origin must respond" window → 504. SSE was tried
    // and reverted (2026-05-16) because Cloudflare buffered/corrupted the
    // chunked stream. Chunking the NON-streaming call keeps each request
    // small and well under the gateway timeout, preserves partial success,
    // and avoids the SSE buffering problem entirely.
    const REPLACE_CHUNK = 15;
    setProcessingStatus(`Minting ${oldUsns.length} replacement USN(s)…`);
    const apiClientMod = (await import('@/services/apiClient')).default;
    const batch = { replacements: [], failures: [], replaced_count: 0, failed_count: 0 };
    let chunkError = null;
    for (let i = 0; i < oldUsns.length; i += REPLACE_CHUNK) {
      const chunk = oldUsns.slice(i, i + REPLACE_CHUNK);
      setProcessingStatus(`Minting replacement USNs ${i + 1}–${Math.min(i + REPLACE_CHUNK, oldUsns.length)} of ${oldUsns.length}…`);
      try {
        const resp = await apiClientMod.post(
          '/usn-replacement/replace-batch',
          {
            old_usns: chunk,
            reason: dialog.reason,
            source_flow: sourceFlow,
            linked_documents: { source_page: `FGUSNLabelReprint:${queueLabel || 'Batch'}` },
          },
          { timeout: 120000 },
        );
        const d = resp.data || {};
        batch.replacements.push(...(d.replacements || []));
        batch.failures.push(...(d.failures || []));
      } catch (e) {
        chunkError = e;
        const status = e?.response?.status;
        const detail = e?.response?.data?.detail || e?.response?.data?.message || e?.message || 'Replace failed (no response from server)';
        const detailStr = typeof detail === 'string' ? detail : JSON.stringify(detail);
        // Pool-empty is terminal for the whole run — surface and stop.
        if (/REPLACE.*pool|Insufficient USNs/i.test(detailStr)) {
          toast.error(
            `${detailStr}\n\nFix: Admin → USN Master Import (section="REPLACE").`,
            { duration: 12000, data: { testid: 'replace-batch-error-toast' } },
          );
          if (batch.replacements.length === 0) return;
          break;
        }
        // Otherwise record this chunk's USNs as failed and stop further chunks.
        chunk.forEach((u) => batch.failures.push({ old_usn: u, status_code: status || 0, detail: detailStr }));
        break;
      }
    }
    batch.replaced_count = batch.replacements.length;
    batch.failed_count = batch.failures.length;

    if (!batch.replacements.length) {
      const status = chunkError?.response?.status;
      const detail = chunkError?.response?.data?.detail || chunkError?.message || 'See backend logs.';
      const detailStr = typeof detail === 'string' ? detail : JSON.stringify(detail);
      toast.error(
        `No USNs were minted.${status ? ` (HTTP ${status})` : ''}\n\n${detailStr}`,
        { duration: 14000, data: { testid: 'replace-batch-error-toast' } },
      );
      return;
    }
    if (chunkError) {
      toast.warning(
        `Minted ${batch.replacements.length} USN(s); the rest could not be processed${chunkError?.response?.status ? ` (HTTP ${chunkError.response.status})` : ''}. Printing the successful ones.`,
        { duration: 10000 },
      );
    } else if (batch.failed_count) {
      const firstDetail = batch.failures?.[0]?.detail || 'unknown reason';
      toast.warning(
        batch.failed_count === 1
          ? `Replace failed: ${firstDetail}`
          : `${batch.failed_count} replace failures — first: ${firstDetail}`,
        { duration: 10000 }
      );
      console.warn('USN replace failures:', batch.failures);
    }
    const oldToNew = Object.fromEntries(batch.replacements.map((r) => [r.old_usn, r.new_usn]));

    setIsPrinting(true);
    const printerConfig = PRINTER_CONFIGS[printerModel];
    let innerBlob = '';
    const boxPlans = []; // [{ newUsn, label_data, mrp_formatted, template? }]
    let printedCount = 0;
    let failedCount = 0;

    try {
      // ---- STEP 2: per-USN fetch label data + accumulate blobs ----
      for (let i = 0; i < queue.length; i++) {
        setDtPrintingIndex(i);
        const label = queue[i];
        const newUsn = oldToNew[label.usn_code];
        if (!newUsn) {
          failedCount++;
          setSessionStats((prev) => ({ ...prev, failed: prev.failed + 1 }));
          continue;
        }
        setProcessingStatus(`Generating label ${i + 1} of ${queue.length}...`);
        try {
          const reprintResponse = await apiClient.post('/main-labels/usn-reprint/generate', {
            usn_code: newUsn,
            pricing_type: chosenPricing || undefined,
            template_id: productTemplateId || undefined,
          });
          const { label_data, template, box_template, mrp_formatted, usn_code } = reprintResponse.data;
          innerBlob += generateTsplFromLabelData(
            label_data, template, usn_code, mrp_formatted,
            null, printerConfig, template?.template_type,
          );
          if (box_template) {
            // Combo: stash for Phase B. Pair number is unknown here, default 1.
            boxPlans.push({
              newUsn: usn_code,
              label_data,
              mrp_formatted,
              auto_box_template: box_template,
            });
          }
          printedCount++;
          setSessionStats((prev) => ({ ...prev, reprinted: prev.reprinted + 1 }));
        } catch (genErr) {
          console.error(`Failed to generate label ${label.usn_code}:`, genErr);
          failedCount++;
          setSessionStats((prev) => ({ ...prev, failed: prev.failed + 1 }));
        }
      }

      if (!innerBlob && !boxPlans.length) {
        toast.error('No labels could be generated.');
        return;
      }

      // ---- STEP 3: stream product (inner) blob to product printer ----
      if (innerBlob) {
        setProcessingStatus(`Streaming ${printedCount} product sticker(s)…`);
        try {
          await printerApi.sendToUSBPrinter(productDevice, innerBlob);
        } catch (e) {
          toast.error(`Product-sticker print error: ${e.message || e}`);
        }
      }

      // ---- STEP 4: Phase B prompt + box-sticker blob (combo only) ----
      if (boxPlans.length > 0) {
        // Suggest a box template — default to the most-common one returned by the API.
        const counts = {};
        for (const bp of boxPlans) {
          const nm = bp.auto_box_template?.template_name;
          if (nm) counts[nm] = (counts[nm] || 0) + 1;
        }
        const suggestedBoxTemplateName = Object.entries(counts)
          .sort((a, b) => b[1] - a[1])[0]?.[0] || '';
        const suggestedBoxTemplateId = templates.find(
          (t) => t.template_name === suggestedBoxTemplateName,
        )?.template_id || '';

        setProcessingStatus(
          `Product done. Waiting for operator to connect box-label printer for ${boxPlans.length} combo USN(s)…`
        );
        const phaseB = await new Promise((resolve) => {
          setDtPhaseBComboCount(boxPlans.length);
          setDtPhaseBTemplate(suggestedBoxTemplateId);
          setDtPhaseBDevice(null);
          setDtPhaseBResolve(() => resolve);
          setDtPhaseBOpen(true);
        });
        if (!phaseB || phaseB.cancelled) {
          toast.info(`Box-sticker phase cancelled. ${boxPlans.length} combo box sticker(s) NOT printed.`);
        } else {
          const boxDevice = phaseB.device || productDevice;
          const boxTplObj = templates.find((t) => t.template_id === phaseB.templateId);
          let boxBlob = '';
          for (const bp of boxPlans) {
            const tpl = boxTplObj || bp.auto_box_template;
            if (!tpl) continue;
            boxBlob += generateTsplFromLabelData(
              bp.label_data, tpl, bp.newUsn, bp.mrp_formatted,
              null, printerConfig, tpl?.template_type,
            );
          }
          if (boxBlob) {
            setProcessingStatus(`Streaming ${boxPlans.length} box sticker(s)…`);
            try {
              await printerApi.sendToUSBPrinter(boxDevice, boxBlob);
            } catch (e) {
              toast.error(`Box-sticker print error: ${e.message || e}. Product stickers were already printed.`);
            }
          }
        }
      }

      toast.success(`Printed ${printedCount} labels${failedCount > 0 ? ` (${failedCount} failed)` : ''}`);
      // Clear queue after printing
      setQueue([]);
      setDtPrintingIndex(-1);
    } catch (error) {
      console.error('Batch print error:', error);
      toast.error('Failed to complete batch printing');
    } finally {
      setIsPrinting(false);
      setDtPrintingIndex(-1);
      setProcessingStatus('');
    }
  };

  // Thin wrappers that bind the shared print function to each queue.
  const handlePrintAllDTLabels = () => _handlePrintQueue({
    queue: dtLabelsQueue,
    setQueue: setDtLabelsQueue,
    sourceFlow: 'fg_design_transfer_reprint',
    queueLabel: 'DesignTransferQueue',
    subjectLabel: `Design Transfer relabel — ${dtLabelsQueue.length} USN(s)`,
  });
  const handlePrintAllManualBatch = () => _handlePrintQueue({
    queue: manualBatchQueue,
    setQueue: setManualBatchQueue,
    sourceFlow: presetSourceFlow || 'fg_manual_batch_reprint',
    queueLabel: presetSourceFlow === 'contaminated_bulk_reprint' ? 'ContaminatedBulkReprint' : 'ManualBatchQueue',
    subjectLabel: presetSourceFlow === 'contaminated_bulk_reprint'
      ? `Contaminated USN bulk reprint — ${manualBatchQueue.length} USN(s)`
      : `Manual batch reprint — ${manualBatchQueue.length} USN(s)`,
  });

  // 2026-05-26 — Preload the manual-batch queue from a navigation
  // preset (currently used by FGPutAwaySupervisor → Bulk Reprint
  // Contaminated USNs). Runs ONCE on mount.
  useEffect(() => {
    if (presetBatch.length === 0) return;
    setBatchMode(true);  // ensure the batch UI is visible
    setManualBatchQueue(prev => {
      const seen = new Set(prev.map(l => l.usn_code));
      const additions = presetBatch
        .filter(p => p.usn_code && !seen.has(p.usn_code))
        .map(p => ({
          usn_code: p.usn_code,
          pair_number: p.pair_number || null,
          is_combo: !!p.is_combo,
          is_contaminated: !!p.is_contaminated,
          // Place-holders so the existing queue renderer doesn't crash
          // when it expects design_name/mrp; the per-USN
          // /main-labels/usn-reprint/generate call will overwrite these
          // during _handlePrintQueue.
          design_name: '(will be loaded at print time)',
          mrp_formatted: '',
        }));
      return [...prev, ...additions];
    });
    if (presetBatch.length > 0) {
      toast.info(`Loaded ${presetBatch.length} contaminated USN(s). Connect printer and click Print Batch.`, { duration: 8000 });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="min-h-screen bg-[#FFFBF5]">
      {/* Header */}
      <header className="bg-[#1A1F2E] text-[#F9FAFB] shadow-lg">
        <div className="max-w-7xl mx-auto px-4 md:px-6 h-16 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button 
              variant="ghost" 
              onClick={() => navigate('/wms/fg')} 
              className="text-[#F9FAFB] hover:bg-white/10"
              data-testid="back-button"
            >
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <div className="flex items-center gap-3">
              <Tag className="w-7 h-7" />
              <div>
                <h1 className="text-xl font-bold font-display">USN Label Reprint</h1>
                <p className="text-sm text-[#E8DCC8]">Reprint labels for existing USNs</p>
              </div>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <PrintMethodToggle screenKey="fg-usn-reprint" dark />
            {usbDevice ? (
              <Badge variant="outline" className="border-green-400 text-green-400">
                <Usb className="w-3 h-3 mr-1" />
                {usbDevice.productName || 'Connected'}
              </Badge>
            ) : (
              <Button
                variant="outline"
                size="sm"
                onClick={connectUSBPrinter}
                className="border-yellow-400 text-yellow-400 hover:bg-yellow-400/10"
              >
                <Usb className="w-3 h-3 mr-1" />
                Connect Printer
              </Button>
            )}
            <Button
              variant="outline"
              onClick={() => setShowSettings(true)}
              className="border-[#E8DCC8] text-[#E8DCC8] hover:bg-white/10"
              data-testid="settings-btn"
            >
              <Settings className="w-4 h-4" />
            </Button>
            <Button
              variant="outline"
              onClick={handleShowHistory}
              className="border-[#E8DCC8] text-[#E8DCC8] hover:bg-white/10"
              data-testid="history-btn"
            >
              <History className="w-4 h-4" />
            </Button>
          </div>
        </div>
      </header>
      
      {/* Main Content */}
      <main className="max-w-2xl mx-auto px-4 md:px-6 py-8">
        {/* Damage Section reprint banner — informs operator about applied defaults */}
        {fromDamage && (
          <Alert className="mb-4 bg-rose-50 border-rose-300" data-testid="damage-mode-banner">
            <AlertTriangle className="h-4 w-4 text-rose-600" />
            <AlertTitle className="text-rose-800">Damage Section Reprint</AlertTitle>
            <AlertDescription className="text-rose-700">
              Defaults applied: Pricing Type = <strong>TN</strong>, Product Template = <strong>DHOTI - FG</strong>.
              Select a Box Template only if this is a box item.
            </AlertDescription>
          </Alert>
        )}
        {/* Design Transfer Batch Printing */}
        {dtLabelsQueue.length > 0 && (
          <Card className="mb-6 shadow-xl border-2 border-orange-400" data-testid="dt-batch-card">
            <CardHeader className="bg-gradient-to-r from-orange-500 to-amber-500 text-white">
              <CardTitle className="flex items-center gap-2 text-xl">
                <Tag className="w-5 h-5" />
                Design Transfer Labels ({dtLabelsQueue.length})
              </CardTitle>
              <CardDescription className="text-orange-100">
                {dtPrintingIndex >= 0 
                  ? `Printing label ${dtPrintingIndex + 1} of ${dtLabelsQueue.length}...` 
                  : 'Connect USB printer and print all labels'}
              </CardDescription>
            </CardHeader>
            <CardContent className="p-4">
              <div className="space-y-3">
                {/* 2026-05-15 — REPLACE pool indicator. Most common
                    cause of "Replace failed" toast is this pool being
                    empty. Show it upfront so the operator can import
                    more REPLACE USNs before clicking Print All. */}
                {replacePool && (
                  <div
                    data-testid="replace-pool-banner"
                    className={`flex items-center justify-between gap-3 rounded border p-2 text-sm ${
                      replacePool.available < dtLabelsQueue.length
                        ? 'border-rose-300 bg-rose-50 text-rose-900'
                        : replacePool.available < dtLabelsQueue.length * 3
                        ? 'border-amber-300 bg-amber-50 text-amber-900'
                        : 'border-emerald-300 bg-emerald-50 text-emerald-900'
                    }`}
                  >
                    <span className="flex items-center gap-2">
                      {replacePool.available < dtLabelsQueue.length ? (
                        <AlertTriangle className="w-4 h-4" />
                      ) : (
                        <CheckCircle className="w-4 h-4" />
                      )}
                      <span>
                        REPLACE pool: <b>{replacePool.available}</b> available
                        {' '}({replacePool.used} used of {replacePool.total} total).
                        {' '}Need {dtLabelsQueue.length} for this batch.
                      </span>
                    </span>
                    {replacePool.available < dtLabelsQueue.length && (
                      <span className="text-xs italic">
                        Import more via Admin → USN Master Import (section=&ldquo;REPLACE&rdquo;) before printing.
                      </span>
                    )}
                  </div>
                )}
                {/* Label List */}
                <div className="max-h-48 overflow-y-auto border rounded">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead className="py-2">USN</TableHead>
                        <TableHead className="py-2">Design</TableHead>
                        <TableHead className="py-2">Shade/Size</TableHead>
                        <TableHead className="py-2 text-center">Status</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {dtLabelsQueue.map((label, idx) => (
                        <TableRow key={label.usn_code} className={idx === dtPrintingIndex ? 'bg-orange-50' : ''}>
                          <TableCell className="py-1 font-mono text-sm">{label.usn_code}</TableCell>
                          <TableCell className="py-1 text-sm">{label.design_name}</TableCell>
                          <TableCell className="py-1 text-sm text-gray-600">{label.shade}/{label.size}</TableCell>
                          <TableCell className="py-1 text-center">
                            {idx < dtPrintingIndex ? (
                              <CheckCircle className="w-4 h-4 text-green-500 inline" />
                            ) : idx === dtPrintingIndex ? (
                              <RefreshCw className="w-4 h-4 text-orange-500 animate-spin inline" />
                            ) : (
                              <span className="text-gray-400">-</span>
                            )}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
                
                {/* Actions */}
                <div className="flex gap-2 justify-end">
                  <Button
                    variant="outline"
                    onClick={() => { setDtLabelsQueue([]); setDtPrintingIndex(-1); }}
                    className="border-gray-400"
                  >
                    <X className="w-4 h-4 mr-1" />
                    Clear
                  </Button>
                  <Button
                    onClick={handlePrintAllDTLabels}
                    disabled={!usbDevice || isPrinting}
                    className="bg-orange-600 hover:bg-orange-700 text-white"
                  >
                    <Printer className="w-4 h-4 mr-1" />
                    {isPrinting ? 'Printing...' : `Print All (${dtLabelsQueue.length})`}
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Session Stats */}
        <div className="flex justify-center gap-6 mb-6">
          <div className="text-center">
            <div className="text-3xl font-bold text-green-600">{sessionStats.reprinted}</div>
            <div className="text-sm text-gray-500">Reprinted</div>
          </div>
          <div className="text-center">
            <div className="text-3xl font-bold text-red-600">{sessionStats.failed}</div>
            <div className="text-sm text-gray-500">Failed</div>
          </div>
        </div>
        
        {/* Main Scan Card */}
        <Card className="mb-6 shadow-xl border-2 border-teal-200" data-testid="scan-usn-card">
          <CardHeader className="bg-gradient-to-r from-teal-500 to-cyan-500 text-white">
            <div className="flex items-center justify-between gap-4">
              <div>
                <CardTitle className="flex items-center gap-2 text-2xl">
                  <Scan className="w-6 h-6" />
                  {batchMode ? 'Scan Multiple USNs (Batch Mode)' : 'Scan or Enter USN'}
                </CardTitle>
                <CardDescription className="text-teal-100">
                  {batchMode
                    ? 'Every scan auto-adds to the batch. Click Print Batch when done. Combo USNs trigger product + box prompts.'
                    : 'Enter an existing USN to fetch details and reprint the label'}
                </CardDescription>
              </div>
              {/* 2026-05-16 — Batch Mode toggle. ON = accumulate scans
                   into manualBatchQueue. OFF = single-USN flow as before. */}
              <label
                className="flex items-center gap-2 cursor-pointer bg-white/15 hover:bg-white/25 rounded-full px-3 py-1.5 text-sm font-medium select-none transition-colors"
                data-testid="batch-mode-toggle"
              >
                <input
                  type="checkbox"
                  checked={batchMode}
                  onChange={(e) => setBatchMode(e.target.checked)}
                  className="accent-white"
                />
                <span>Batch Mode</span>
              </label>
            </div>
          </CardHeader>
          <CardContent className="p-6">
            <div className="space-y-4">
              {/* USN Input */}
              <div className="flex gap-2">
                <Input
                  ref={scanInputRef}
                  value={usnCode}
                  onChange={(e) => setUsnCode(e.target.value.toUpperCase())}
                  onKeyPress={(e) => e.key === 'Enter' && handleSearchUSN()}
                  placeholder="Scan or type USN code..."
                  className="h-14 text-xl text-center font-mono tracking-wider flex-1"
                  disabled={isSearching || isPrinting}
                  autoFocus
                  data-testid="usn-input"
                />
                <Button
                  onClick={handleSearchUSN}
                  disabled={isSearching || isPrinting || !usnCode.trim()}
                  className="h-14 px-6 bg-teal-600 hover:bg-teal-700"
                  data-testid="search-btn"
                >
                  {isSearching ? (
                    <RefreshCw className="w-5 h-5 animate-spin" />
                  ) : (
                    <Search className="w-5 h-5" />
                  )}
                </Button>
              </div>
              
              {(isSearching || isPrinting) && (
                <div className="flex items-center justify-center gap-3 py-4">
                  <RefreshCw className="w-6 h-6 animate-spin text-teal-600" />
                  <span className="text-lg font-medium text-teal-700">{processingStatus || 'Searching...'}</span>
                </div>
              )}
              
              {!usbDevice && !isSearching && !isPrinting && (
                <Alert className="bg-yellow-50 border-yellow-300">
                  <AlertTriangle className="h-4 w-4 text-yellow-600" />
                  <AlertTitle className="text-yellow-800">Printer Not Connected</AlertTitle>
                  <AlertDescription className="text-yellow-700">
                    Click "Connect Printer" in the header to connect your USB printer first.
                  </AlertDescription>
                </Alert>
              )}
            </div>
          </CardContent>
        </Card>

        {/* 2026-05-16 — Manual Batch Queue Card. Shown only when Batch
            Mode is ON. Lists every scanned USN, lets the operator
            remove individual entries, and triggers the shared two-phase
            print pipeline via Print Batch. Combo USNs in the queue
            automatically trigger Phase B (box-sticker) prompts. */}
        {batchMode && (
          <Card className="mb-6 shadow-lg border-2 border-emerald-200" data-testid="manual-batch-queue-card">
            <CardHeader className="bg-emerald-50 border-b border-emerald-200 flex flex-row items-center justify-between">
              <div>
                <CardTitle className="flex items-center gap-2 text-emerald-800">
                  <Package className="w-5 h-5" />
                  Scan Batch ({manualBatchQueue.length})
                </CardTitle>
                <CardDescription className="text-emerald-700">
                  {manualBatchQueue.length === 0
                    ? 'Scan or type a USN above and press Enter to add it here.'
                    : `Ready to print. ${manualBatchQueue.filter((l) => l.is_combo).length} combo(s) in batch — Phase B (box stickers) will be prompted automatically.`}
                </CardDescription>
              </div>
              <div className="flex items-center gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleClearManualBatch}
                  disabled={manualBatchQueue.length === 0 || isPrinting}
                  data-testid="manual-batch-clear-btn"
                >
                  Clear
                </Button>
                <Button
                  size="sm"
                  onClick={handlePrintAllManualBatch}
                  disabled={manualBatchQueue.length === 0 || !usbDevice || isPrinting}
                  className="bg-emerald-600 hover:bg-emerald-700 text-white"
                  data-testid="manual-batch-print-all-btn"
                >
                  <Printer className="w-4 h-4 mr-2" />
                  Print Batch ({manualBatchQueue.length})
                </Button>
              </div>
            </CardHeader>
            <CardContent className="p-0">
              {/* Pool depth banner — shows when REPLACE pool can't cover the batch. */}
              {replacePool && manualBatchQueue.length > 0 && (
                <div
                  data-testid="manual-batch-replace-pool-banner"
                  className={`flex items-center justify-between gap-3 m-3 rounded border p-2 text-sm ${
                    replacePool.available < manualBatchQueue.length
                      ? 'border-rose-300 bg-rose-50 text-rose-900'
                      : replacePool.available < manualBatchQueue.length * 3
                      ? 'border-amber-300 bg-amber-50 text-amber-900'
                      : 'border-emerald-300 bg-emerald-50 text-emerald-900'
                  }`}
                >
                  <span className="flex items-center gap-2">
                    {replacePool.available < manualBatchQueue.length ? (
                      <AlertTriangle className="w-4 h-4" />
                    ) : (
                      <CheckCircle className="w-4 h-4" />
                    )}
                    <span>
                      REPLACE pool: <b>{replacePool.available}</b> available
                      {' '}({replacePool.used} used of {replacePool.total} total).
                      {' '}Need {manualBatchQueue.length} for this batch.
                    </span>
                  </span>
                </div>
              )}
              {manualBatchQueue.length === 0 ? (
                <div className="py-8 text-center text-stone-500 text-sm italic">
                  Queue is empty — scan a USN to begin.
                </div>
              ) : (
                <div className="max-h-72 overflow-y-auto divide-y divide-stone-100">
                  {manualBatchQueue.map((label, idx) => (
                    <div
                      key={label.usn_code}
                      className="flex items-center justify-between px-4 py-2 hover:bg-stone-50"
                      data-testid={`manual-batch-row-${idx}`}
                    >
                      <div className="flex items-center gap-3">
                        <span className="text-stone-400 text-xs font-mono w-6 text-right">{idx + 1}.</span>
                        <span className="font-mono font-semibold text-stone-800">{label.usn_code}</span>
                        {label.pair_number != null && (
                          <span
                            className="inline-flex items-center rounded-md bg-indigo-100 px-2 py-0.5 text-[10px] font-semibold tracking-wide text-indigo-800 border border-indigo-300"
                            data-testid={`manual-batch-pair-${idx}`}
                          >
                            Pair {label.pair_number}
                          </span>
                        )}
                        {label.design_name && (
                          <span className="text-sm text-stone-600">
                            {label.design_name}
                            {label.shade ? ` / ${label.shade}` : ''}
                            {label.size ? ` / ${label.size}` : ''}
                          </span>
                        )}
                        {label.is_combo && (
                          <span className="inline-flex items-center rounded-full bg-purple-100 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-purple-800 border border-purple-300">
                            COMBO
                          </span>
                        )}
                        {label.is_contaminated && (
                          <span
                            className="inline-flex items-center rounded-full bg-rose-100 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-rose-800 border border-rose-300"
                            title="Contaminated USN — recovering via fresh code"
                          >
                            CONTAMINATED
                          </span>
                        )}
                      </div>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleRemoveFromManualBatch(label.usn_code)}
                        disabled={isPrinting}
                        className="text-rose-600 hover:bg-rose-50"
                        data-testid={`manual-batch-remove-${idx}`}
                      >
                        ✕
                      </Button>
                    </div>
                  ))}
                </div>
              )}
              {batchAddingUsn && (
                <div className="flex items-center justify-center gap-2 text-sm text-stone-500 py-2">
                  <RefreshCw className="w-3.5 h-3.5 animate-spin" /> Adding USN…
                </div>
              )}
            </CardContent>
          </Card>
        )}
        
        {/* USN Details Card */}
        {usnDetails && (
          <Card className="mb-6 shadow-lg border-2 border-blue-200" data-testid="usn-details-card">
            <CardHeader className="bg-blue-50 border-b border-blue-200 flex flex-row items-center justify-between">
              <div>
                <CardTitle className="flex items-center gap-2 text-blue-800">
                  <Package className="w-5 h-5" />
                  USN Details
                </CardTitle>
                <CardDescription className="text-blue-600 font-mono text-lg mt-1">
                  {usnDetails.usn_code}
                </CardDescription>
              </div>
              <Button
                variant="ghost"
                size="sm"
                onClick={handleClear}
                className="text-gray-500 hover:text-gray-700"
              >
                <X className="w-4 h-4" />
              </Button>
            </CardHeader>
            <CardContent className="p-4 space-y-4">
              {/* Item Details */}
              {usnDetails.item && (
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label className="text-xs text-gray-500">Design Name</Label>
                    <p className="font-medium">{usnDetails.item.design_name || '-'}</p>
                  </div>
                  <div>
                    <Label className="text-xs text-gray-500">Shade</Label>
                    <p className="font-medium">{usnDetails.item.shade || '-'}</p>
                  </div>
                  <div>
                    <Label className="text-xs text-gray-500">Size</Label>
                    <p className="font-medium">{usnDetails.item.size || '-'}</p>
                  </div>
                  <div>
                    <Label className="text-xs text-gray-500">Description</Label>
                    <p className="font-medium text-sm">{usnDetails.item.description || '-'}</p>
                  </div>
                </div>
              )}
              
              <div className="border-t pt-3 space-y-3">
                {/* Pricing Type Selector */}
                <div className="flex items-center gap-3">
                  <Label className="text-sm font-medium w-28">Pricing Type:</Label>
                  <Select value={pricingType} onValueChange={setPricingType}>
                    <SelectTrigger className="flex-1" data-testid="pricing-type-select">
                      <SelectValue placeholder="Select pricing type" />
                    </SelectTrigger>
                    <SelectContent>
                      {availablePricingTypes.map((pt) => (
                        <SelectItem key={pt} value={pt}>{pt}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                
                {/* Template Selector (Product) */}
                <div className="flex items-center gap-3">
                  <Label className="text-sm font-medium w-28">Product Template:</Label>
                  <Select value={selectedTemplate} onValueChange={setSelectedTemplate}>
                    <SelectTrigger className="flex-1" data-testid="template-select">
                      <SelectValue placeholder="Use original template" />
                    </SelectTrigger>
                    <SelectContent>
                      {templates.map((t) => (
                        <SelectItem key={t.template_id} value={t.template_id}>
                          {t.template_name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                {/* Box Template Selector — optional. Selecting this triggers
                    the dual-print flow (Product sticker → Box sticker on the
                    SAME replaced USN). Leave blank for non-box items. */}
                <div className="flex items-center gap-3">
                  <Label className="text-sm font-medium w-28">Box Template:</Label>
                  <Select
                    value={boxTemplate || 'none'}
                    onValueChange={(v) => setBoxTemplate(v === 'none' ? '' : v)}
                  >
                    <SelectTrigger className="flex-1" data-testid="box-template-select">
                      <SelectValue placeholder="None — single sticker" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">None — single sticker</SelectItem>
                      {templates.map((t) => (
                        <SelectItem key={`box-${t.template_id}`} value={t.template_id}>
                          {t.template_name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                {boxTemplate && (
                  <p className="text-xs text-amber-700 -mt-1 ml-28 pl-3">
                    Box item: product sticker will print first, then a prompt will appear to print the box sticker on the same replaced USN.
                  </p>
                )}
              </div>
              
              {/* Reprint Count Warning */}
              {usnDetails.reprint_count > 0 && (
                <Alert className="bg-amber-50 border-amber-300">
                  <AlertTriangle className="h-4 w-4 text-amber-600" />
                  <AlertDescription className="text-amber-700">
                    This USN has been reprinted <strong>{usnDetails.reprint_count}</strong> time(s) before.
                  </AlertDescription>
                </Alert>
              )}
              
              {/* Reprint Button */}
              <Button
                onClick={handleReprint}
                disabled={isPrinting || !usbDevice}
                className="w-full h-12 bg-teal-600 hover:bg-teal-700 text-lg"
                data-testid="reprint-btn"
              >
                {isPrinting ? (
                  <>
                    <RefreshCw className="w-5 h-5 mr-2 animate-spin" />
                    Reprinting...
                  </>
                ) : (
                  <>
                    <Printer className="w-5 h-5 mr-2" />
                    Reprint Label
                  </>
                )}
              </Button>
            </CardContent>
          </Card>
        )}
        
        {/* Last Reprinted */}
        {lastReprinted && (
          <Card className="mb-6 bg-green-50 border-green-200" data-testid="last-reprinted-card">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <CheckCircle className="w-8 h-8 text-green-600" />
                <div className="flex-1">
                  <div className="font-bold text-green-800 text-lg font-mono">
                    {lastReprinted.usn_code}
                  </div>
                  <div className="text-sm text-green-600">
                    {lastReprinted.design_name || 'Label reprinted successfully'}
                  </div>
                </div>
                <div className="text-xs text-green-500">
                  {lastReprinted.timestamp.toLocaleTimeString()}
                </div>
              </div>
            </CardContent>
          </Card>
        )}
        
        {/* Quick Instructions */}
        <Card className="shadow-sm bg-gray-50">
          <CardContent className="p-4">
            <h3 className="font-semibold text-gray-700 mb-2 flex items-center gap-2">
              <Scan className="w-4 h-4" />
              How to Reprint
            </h3>
            <ol className="list-decimal list-inside space-y-1 text-sm text-gray-600">
              <li>Connect USB printer (one-time)</li>
              <li>Scan or type the USN code</li>
              <li>Verify item details</li>
              <li>Optionally change pricing type or product template</li>
              <li>For <strong>box items</strong>, also pick a Box Template — both stickers will print on the same replaced USN</li>
              <li>Click "Reprint Label"</li>
            </ol>
            <div className="mt-3 p-2 bg-blue-50 rounded border border-blue-200">
              <p className="text-xs text-blue-700">
                <strong>Note:</strong> USNs must exist in dispatch section records to be reprinted.
              </p>
            </div>
          </CardContent>
        </Card>
      </main>
      
      {/* 2026-05-14 — Design Transfer Phase A "Pre-Print" prompt.
          Operator picks pricing, product template, and product printer
          before any TSPL is sent. Start is disabled until all three are
          set. */}
      <Dialog
        open={dtPhaseAOpen}
        onOpenChange={(open) => {
          if (!open && dtPhaseAResolve) {
            dtPhaseAResolve({ cancelled: true });
            setDtPhaseAResolve(null);
            setDtPhaseADevice(null);
          }
          setDtPhaseAOpen(open);
        }}
      >
        <DialogContent className="max-w-md" data-testid="dt-phase-a-dialog">
          <DialogHeader>
            <DialogTitle>Confirm pricing, template &amp; printer</DialogTitle>
            <DialogDescription>
              Design Transfer relabel — {dtLabelsQueue.length} USN(s) queued.
              Pick the pricing rate-card, product sticker template, and connect the
              product printer to start.
            </DialogDescription>
          </DialogHeader>
          <div className="py-2 space-y-3 text-sm">
            <div className="space-y-1">
              <label className="text-xs font-medium text-gray-600">Pricing Level</label>
              <Select value={dtPhaseAPricing} onValueChange={setDtPhaseAPricing}>
                <SelectTrigger data-testid="dt-phase-a-pricing-select">
                  <SelectValue placeholder="Select pricing level" />
                </SelectTrigger>
                <SelectContent>
                  {(availablePricingTypes.length ? availablePricingTypes : ['TN', 'KA', 'MRP']).map((p) => (
                    <SelectItem key={p} value={p}>{p}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1">
              <label className="text-xs font-medium text-gray-600">Product Sticker Template</label>
              <Select value={dtPhaseATemplate} onValueChange={setDtPhaseATemplate}>
                <SelectTrigger data-testid="dt-phase-a-template-select">
                  <SelectValue placeholder="Select template" />
                </SelectTrigger>
                <SelectContent>
                  {templates.map((t) => (
                    <SelectItem key={t.template_id} value={t.template_id}>
                      {t.template_name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1">
              <label className="text-xs font-medium text-gray-600">Product Printer</label>
              <div className="flex items-center gap-2">
                <Button
                  size="sm"
                  variant="outline"
                  disabled={dtPhaseAConnecting}
                  onClick={async () => {
                    setDtPhaseAConnecting(true);
                    try {
                      const dev = await printerApi.requestUSBPrinter();
                      setDtPhaseADevice(dev);
                      toast.success(`Product printer connected: ${dev.productName || 'USB printer'}`);
                    } catch (e) {
                      toast.error(`Connect failed: ${e.message || e}`);
                    } finally {
                      setDtPhaseAConnecting(false);
                    }
                  }}
                  data-testid="dt-phase-a-connect-printer-btn"
                  className="border-blue-300 text-blue-700 hover:bg-blue-50"
                >
                  {dtPhaseAConnecting ? (
                    <RefreshCw className="w-3.5 h-3.5 mr-1 animate-spin" />
                  ) : (
                    <Printer className="w-3.5 h-3.5 mr-1" />
                  )}
                  {dtPhaseADevice ? 'Re-select Printer' : 'Connect Printer'}
                </Button>
                {dtPhaseADevice && (
                  <Badge
                    variant="outline"
                    className="bg-green-50 text-green-700 border-green-300"
                    data-testid="dt-phase-a-printer-badge"
                  >
                    <CheckCircle className="w-3 h-3 mr-1" />
                    {dtPhaseADevice.productName || 'USB printer'}
                  </Badge>
                )}
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                if (dtPhaseAResolve) dtPhaseAResolve({ cancelled: true });
                setDtPhaseAResolve(null);
                setDtPhaseADevice(null);
                setDtPhaseAOpen(false);
              }}
              data-testid="dt-phase-a-cancel-btn"
            >
              Cancel
            </Button>
            <Button
              disabled={!dtPhaseADevice || !dtPhaseATemplate || !dtPhaseAPricing}
              onClick={() => {
                if (dtPhaseAResolve) {
                  dtPhaseAResolve({
                    cancelled: false,
                    device: dtPhaseADevice,
                    templateId: dtPhaseATemplate,
                    pricing: dtPhaseAPricing,
                  });
                }
                setDtPhaseAResolve(null);
                setDtPhaseAOpen(false);
              }}
              className="bg-blue-600 hover:bg-blue-700 text-white disabled:bg-gray-300"
              data-testid="dt-phase-a-start-btn"
            >
              Start Printing
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* 2026-05-14 — Design Transfer Phase B "Box Sticker" prompt.
          Only opens when at least one combo USN is in the batch.
          Operator picks the box-sticker template + connects a (possibly
          different) box-label printer. Cancel skips box printing
          entirely; product stickers were already printed in Phase A. */}
      <Dialog
        open={dtPhaseBOpen}
        onOpenChange={(open) => {
          if (!open && dtPhaseBResolve) {
            dtPhaseBResolve({ cancelled: true });
            setDtPhaseBResolve(null);
            setDtPhaseBDevice(null);
          }
          setDtPhaseBOpen(open);
        }}
      >
        <DialogContent className="max-w-md" data-testid="dt-phase-b-dialog">
          <DialogHeader>
            <DialogTitle>Connect box-label printer to continue</DialogTitle>
            <DialogDescription>
              Product stickers printed. Load the box-label roll on the box-sticker
              printer, pick the box template, then connect the printer to print
              <span className="font-semibold"> {dtPhaseBComboCount} combo box sticker(s)</span>.
            </DialogDescription>
          </DialogHeader>
          <div className="py-2 space-y-3 text-sm">
            <div className="space-y-1">
              <label className="text-xs font-medium text-gray-600">Box Sticker Template</label>
              <Select value={dtPhaseBTemplate} onValueChange={setDtPhaseBTemplate}>
                <SelectTrigger data-testid="dt-phase-b-template-select">
                  <SelectValue placeholder="Select box template" />
                </SelectTrigger>
                <SelectContent>
                  {templates.map((t) => (
                    <SelectItem key={t.template_id} value={t.template_id}>
                      {t.template_name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1">
              <label className="text-xs font-medium text-gray-600">Box Printer</label>
              <div className="flex items-center gap-2">
                <Button
                  size="sm"
                  variant="outline"
                  disabled={dtPhaseBConnecting}
                  onClick={async () => {
                    setDtPhaseBConnecting(true);
                    try {
                      const dev = await printerApi.requestUSBPrinter();
                      setDtPhaseBDevice(dev);
                      toast.success(`Box printer connected: ${dev.productName || 'USB printer'}`);
                    } catch (e) {
                      toast.error(`Connect failed: ${e.message || e}`);
                    } finally {
                      setDtPhaseBConnecting(false);
                    }
                  }}
                  data-testid="dt-phase-b-connect-printer-btn"
                  className="border-blue-300 text-blue-700 hover:bg-blue-50"
                >
                  {dtPhaseBConnecting ? (
                    <RefreshCw className="w-3.5 h-3.5 mr-1 animate-spin" />
                  ) : (
                    <Printer className="w-3.5 h-3.5 mr-1" />
                  )}
                  {dtPhaseBDevice ? 'Re-select Printer' : 'Connect Box Printer'}
                </Button>
                {dtPhaseBDevice && (
                  <Badge
                    variant="outline"
                    className="bg-green-50 text-green-700 border-green-300"
                    data-testid="dt-phase-b-printer-badge"
                  >
                    <CheckCircle className="w-3 h-3 mr-1" />
                    {dtPhaseBDevice.productName || 'USB printer'}
                  </Badge>
                )}
              </div>
            </div>
            <p className="text-xs text-amber-700">
              Click Cancel to skip box stickers. Product stickers were already printed.
            </p>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                if (dtPhaseBResolve) dtPhaseBResolve({ cancelled: true });
                setDtPhaseBResolve(null);
                setDtPhaseBDevice(null);
                setDtPhaseBOpen(false);
              }}
              data-testid="dt-phase-b-cancel-btn"
            >
              Cancel
            </Button>
            <Button
              disabled={!dtPhaseBDevice || !dtPhaseBTemplate}
              onClick={() => {
                if (dtPhaseBResolve) {
                  dtPhaseBResolve({
                    cancelled: false,
                    device: dtPhaseBDevice,
                    templateId: dtPhaseBTemplate,
                  });
                }
                setDtPhaseBResolve(null);
                setDtPhaseBOpen(false);
              }}
              className="bg-emerald-600 hover:bg-emerald-700 text-white disabled:bg-gray-300"
              data-testid="dt-phase-b-continue-btn"
            >
              Continue
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      
      {/* Box Sticker Print Dialog — shows after the product sticker prints
          on a box item. Operator can connect a different USB printer for the
          box sticker, or reuse the current one, then print on the SAME
          replaced USN. */}
      <Dialog
        open={!!pendingBoxPrint}
        onOpenChange={(open) => { if (!open && pendingBoxPrint) handleSkipBoxSticker(); }}
      >
        <DialogContent className="max-w-md" data-testid="box-print-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Package className="w-5 h-5 text-amber-600" />
              Print Box Sticker
            </DialogTitle>
            <DialogDescription>
              Product sticker printed for{' '}
              <span className="font-mono font-semibold">{pendingBoxPrint?.newUsn}</span>.
              Connect or select your <strong>Box Sticker printer</strong>, then print the box sticker
              for the same USN.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-2 text-sm">
            <div className="flex justify-between">
              <span className="text-gray-600">Replaced USN:</span>
              <span className="font-mono font-semibold">{pendingBoxPrint?.newUsn}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-600">Design:</span>
              <span className="font-medium">{pendingBoxPrint?.designName || '-'}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-600">Box Template:</span>
              <span className="font-medium">
                {templates.find((t) => t.template_id === boxTemplate)?.template_name || '-'}
              </span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-600">Current USB:</span>
              <span className="font-medium">
                {usbDevice ? (usbDevice.productName || 'Connected') : 'Not connected'}
              </span>
            </div>
          </div>
          <DialogFooter className="flex flex-col-reverse sm:flex-row gap-2">
            <Button
              variant="ghost"
              onClick={handleSkipBoxSticker}
              disabled={isPrinting}
              data-testid="skip-box-sticker-btn"
            >
              Skip
            </Button>
            <Button
              variant="outline"
              onClick={() => handlePrintBoxSticker(true)}
              disabled={isPrinting}
              data-testid="connect-box-printer-btn"
            >
              <Usb className="w-4 h-4 mr-2" />
              Connect Box Printer & Print
            </Button>
            <Button
              onClick={() => handlePrintBoxSticker(false)}
              disabled={isPrinting || !usbDevice}
              className="bg-amber-600 hover:bg-amber-700"
              data-testid="print-box-sticker-btn"
            >
              {isPrinting ? (
                <><RefreshCw className="w-4 h-4 mr-2 animate-spin" />Printing...</>
              ) : (
                <><Printer className="w-4 h-4 mr-2" />Print Box Sticker</>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Settings Dialog */}
      <Dialog open={showSettings} onOpenChange={setShowSettings}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Settings className="w-5 h-5" />
              Printer Settings
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            {/* Printer Model */}
            <div className="space-y-2">
              <Label>Printer Model</Label>
              <Select value={printerModel} onValueChange={setPrinterModel}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {Object.entries(PRINTER_CONFIGS).map(([key, config]) => (
                    <SelectItem key={key} value={key}>{config.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            {/* USB Status */}
            <div className="p-3 bg-gray-100 rounded-lg">
              <div className="flex items-center justify-between">
                <span className="text-sm text-gray-600">USB Printer</span>
                {usbDevice ? (
                  <Badge variant="outline" className="bg-green-50 text-green-700 border-green-300">
                    {usbDevice.productName || 'Connected'}
                  </Badge>
                ) : (
                  <Button size="sm" variant="outline" onClick={connectUSBPrinter}>
                    <Usb className="w-3 h-3 mr-1" />
                    Connect
                  </Button>
                )}
              </div>
            </div>
          </div>
          
          <DialogFooter>
            <Button onClick={() => setShowSettings(false)} className="w-full">
              <CheckCircle className="w-4 h-4 mr-2" />
              Done
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      
      {/* Reprint History Dialog */}
      <Dialog open={showHistory} onOpenChange={setShowHistory}>
        <DialogContent className="max-w-3xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <History className="w-5 h-5" />
              Reprint History
            </DialogTitle>
          </DialogHeader>
          {historyLoading ? (
            <div className="flex items-center justify-center py-8">
              <RefreshCw className="w-8 h-8 animate-spin text-gray-400" />
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>USN Code</TableHead>
                  <TableHead>Barcode</TableHead>
                  <TableHead>Pricing</TableHead>
                  <TableHead>Reprinted At</TableHead>
                  <TableHead>By</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {reprintHistory.map((item, idx) => (
                  <TableRow key={idx}>
                    <TableCell className="font-mono">{item.usn_code}</TableCell>
                    <TableCell className="font-mono text-sm">{item.item_barcode}</TableCell>
                    <TableCell>{item.pricing_type}</TableCell>
                    <TableCell>{new Date(item.reprinted_at).toLocaleString()}</TableCell>
                    <TableCell>{item.reprinted_by_name || item.reprinted_by}</TableCell>
                  </TableRow>
                ))}
                {reprintHistory.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={5} className="text-center text-gray-500 py-8">
                      No reprint history found
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default FGUSNLabelReprint;
